import Vue from 'vue'
import upperFirst from 'lodash/upperFirst'
import camelCase from 'lodash/camelCase'
import moment from 'moment'
import * as Sentry from "@sentry/vue"
import * as _ from 'lodash'
import {store} from '../apps/default/store.js'

export default {

    // globally register all components in the 'src/components' directory (using kebab-case)
    registerComponents() {
        const requireComponent = require.context(
            // The relative path of the components folder
            '@/components',
            // Whether or not to look in subfolders
            false,
            // The regular expression used to match base component filenames
            /[A-Z]\w+\.(vue|js)$/
        )

        requireComponent.keys().forEach(fileName => {
            // Get component config
            const componentConfig = requireComponent(fileName)

            // Get PascalCase name of component
            const componentName = upperFirst(
                camelCase(
                    // Strip the leading `./` and extension from the filename
                    fileName.replace(/^\.\/(.*)\.\w+$/, '$1')
                )
            )

            const kebabName = componentName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

            // Register component globally
            Vue.component(
                kebabName,
                // Look for the component options on `.default`, which will
                // exist if the component was exported with `export default`,
                // otherwise fall back to module's root.
                componentConfig.default || componentConfig
            )
        })
    },

    // add a custom Vue mixin to allow a given service to be accessed from any
    // child component, e.g. as 'this.$i18n'
    addCustomMixin(name) {
        Vue.mixin({
            beforeCreate() {
                const options = this.$options
                if (options[name]) {
                    this['$' + name] = options[name]
                } else if (options.parent && options.parent['$' + name]) {
                    this['$' + name] = options.parent['$' + name]
                }
            }
        })
    },

    /**
     * Configure vue-resource to attach the current user auth_key to all api requests,
     * and to redirect to login page if a request is unauthorized (see BaseController.php::beforeAction)
     */
    addAuthorizationInterceptor(store, router, user) {
        Vue.http.interceptors.push(function (request, next) {
            if (request.Authorization) {
                // special handling for request to DietID API (see SurveyFormDietidMixin)
                request.headers.set('Authorization', request.Authorization)
            } else {
                request.headers.set('Authorization', store.state.currentUser.auth_key)
                // if this is set, user's login session should never time out
                // (see models/User.php::validateAndExtendAuthKey)
                request.headers.set('Ios-Device-Uuid', localStorage.getItem('uuid'))
                // pass current custom theme to back end
                if (store.state.theme) {
                    request.headers.set('Wc-Custom-Theme', store.state.theme.name)
                }

                next(function (response) {
                    // NB: don't trigger a chain of logout requests
                    if (request.url.indexOf('logout') == -1 && (!response.status || response.status == 401 || response.status == 403)) {
                        // console.log('addAuthorizationInterceptor, logging user out')
                        if (response.status == 401) {
                            user.logoutUser(Vue, store.state.currentUser.id)
                        } else if (response.status == 403) {
                            Vue.toasted.error(
                                'You are not authorized to perform this action',
                                { duration: 5000 }
                            )
                        }
                    }
                })
            }
        })
    },

    // returns date in format of mysql datetime
    mysqlDatetime(d) {
        return moment(d).format("YYYY-MM-DD HH:mm:ss")
    },

    formatDate(d) {
        if (moment(d).isValid()) {
            return moment.utc(d).local().format('MMMM D YYYY, h:mm A')
        } else {
            return ''
        }
    },

    formatDateShort(d) {
        if (moment(d).isValid()) {
            return moment.utc(d).local().format('M/D/YY')
        } else {
            return ''
        }
    },

    formatTimeDetails(date) {
        return moment(date).format('h:mm A')
    },

    formatDayDetails(date) {
        return moment(date).format('MMM D, YYYY')
    },

    dateIsLessThanDaysAgo(d, days) {
        const ago = moment().subtract(days, 'days')
        return moment(d).isAfter(ago)
    },

    dateIsLessThanMonthsAgo(d, months) {
        const ago = moment().subtract(months, 'months')
        return moment(d).isAfter(ago)
    },

    handleError(e, message = undefined) {
        Vue.toasted.error(
          (message ? message : (e ? (e.body ? e.body.message : 'Error') : 'Error')),
          {duration: 5000}
        )
        //console.log('sentry capturing', process.env.VUE_APP_SENTRY_DSN);
        // Sentry.captureException(e)
    },

    handleEMRError(result) {
        if (result && result.issue && Array.isArray(result.issue)) {
            let diagnostics = ''
            _.each(result.issue, (issue) => {
                if (issue && issue.diagnostics) {
                    diagnostics = (diagnostics.length ? '; ' : '') + issue.diagnostics
                }
            })
            if (diagnostics.length) {
                this.handleError(null, diagnostics)
            }
            return false
        }
        return true
    },

    themeIs(theme) {
        return store.state.theme.name === theme
    },
}
